import { createTask, runAgent } from '@/api/agenticsearch'

export default {
  namespaced: true,
  state: {
    events: [],
    loading: false,
    error: null,
    eventSource: null,
    currentTaskId: null,
  },
  mutations: {
    addEvent(state, event) {
      state.events.push(event)
    },
    updateEventByType(state, { type, event }) {
      const existingIndex = state.events.findIndex(e => e.type === type)
      if (existingIndex !== -1) {
        state.events.splice(existingIndex, 1, event)
      } else {
        state.events.push(event)
      }
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    setEventSource(state, eventSource) {
      state.eventSource = eventSource
    },
    setCurrentTaskId(state, taskId) {
      state.currentTaskId = taskId
    },
    clearResults(state) {
      state.events = []
      state.loading = false
      state.error = null
      state.currentTaskId = null
    },
  },
  actions: {
    async search({ commit, dispatch, rootGetters }, { query, files, filters }) {
      await dispatch('clearResults')
      commit('setLoading', true)
      commit('setError', null)

      try {
        // Get user's state from user store, fallback to content_profile_id
        let userState = rootGetters['user/eventMetadata']?.ust || ''
        if (!userState) {
          userState = rootGetters['user/userContentProfileId'] || ''
        }
        const formData = buildSearchFormData(query, files, filters, userState)
        const taskResponse = await createTask(formData)
        const taskId = taskResponse.task_id
        commit('setCurrentTaskId', taskId)

        const eventSource = runAgent(
          taskId,
          getAgentEventCallback(commit),
          (error) => {
            commit('setError', error.message || 'An error occurred during search')
            commit('setLoading', false)
          },
          () => {
            commit('setLoading', false)
          }
        )

        commit('setEventSource', eventSource)
      } catch (error) {
        commit('setError', error.message || 'Failed to start search')
        commit('setLoading', false)
        throw error
      }
    },
    searchAssessments({ dispatch }, { query, files, filters }) {
      const modifiedQuery = `Search for assessments. ${query}`
      return dispatch('search', { query: modifiedQuery, files, filters })
    },
    clearResults({ commit, state }) {
      if (state.eventSource) {
        state.eventSource.close()
        commit('setEventSource', null)
      }
      commit('clearResults')
    },
  },
  getters: {
    events(state) {
      return state.events
    },
    contentResults(state, getters) {
      if (!state.events || !Array.isArray(state.events)) {
        return []
      }
      return state.events.filter(e => e.type === 'content_generated').map(event => event.data)
    },
    assessmentResults(state, getters) {
      return getters.contentResults.filter(event => event.type === 'assessment')
    },
    hasAssessmentResults(state, getters) {
      return getters.assessmentResults.length > 0
    },
    loading(state) {
      return state.loading
    },
    error(state) {
      return state.error
    },
  },
}

function buildSearchFormData(query, files, filters, userState) {
  const formData = new FormData()

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files[]', file)
    })
  }

  const config = {
    prompt: query,
    model: 'gpt-4',
    temperature: 0.7,
  }
  formData.append('config', JSON.stringify(config))

  if (filters) {
    if (filters.studentName) {
      formData.append('studentName', filters.studentName)
    }
    if (filters.gradeLevel) {
      formData.append('gradeLevel', filters.gradeLevel)
    }
    if (filters.subject) {
      formData.append('subject', filters.subject)
    }
    if (filters.category) {
      formData.append('category', filters.category)
    }
  }

  // Always include user's state if available
  if (userState) {
    formData.append('state', userState)
  }

  return formData
}

function getAgentEventCallback(commit) {
  return (data) => {
    try {
      const eventWithTimestamp = {
        ...data,
        timestamp: new Date().toISOString(),
      }

      if (data.type === 'progress' && data.step !== undefined) {
        commit('updateEventByType', { type: 'progress', event: eventWithTimestamp })
      } else if (data.type === 'completed' || data.type === 'error') {
        commit('addEvent', eventWithTimestamp)
        commit('setLoading', false)
      } else {
        commit('addEvent', eventWithTimestamp)
      }
    } catch (error) {
      console.error('Error processing agent event:', error, 'Event data:', data)
      commit('setError', `Error processing event: ${error.message}`)
      commit('setLoading', false)
    }
  }
}
